import { Inject, Injectable } from '@angular/core';
import {
    DEFAULT_LANGUAGE,
    MissingTranslationHandler,
    TranslateCompiler,
    TranslateLoader,
    TranslateParser,
    TranslatePipe,
    TranslateService,
    TranslateStore,
    USE_DEFAULT_LANG,
    USE_EXTEND,
    USE_STORE,
} from '@ngx-translate/core';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root',
})
export class CustomTranslateService extends TranslateService {
    localeDefault = 'fr_FR';
    localeEnglish = 'en_GB';
    languages = environment.languages;
    constructor(
        @Inject(TranslateStore) store: TranslateStore,
        @Inject(TranslateLoader) currentLoader: TranslateLoader,
        @Inject(TranslateCompiler) compiler: TranslateCompiler,
        @Inject(TranslateParser) parser: TranslateParser,
        @Inject(MissingTranslationHandler) missingTranslationHandler: MissingTranslationHandler,
        @Inject(USE_DEFAULT_LANG) useDefaultLang: boolean = true,
        @Inject(USE_STORE) useStore: boolean = true,
        @Inject(USE_EXTEND) useExtend: boolean = false,
        @Inject(DEFAULT_LANGUAGE) defaultLang: string = 'fr',
        @Inject(TranslatePipe) private translatePipe: TranslatePipe
    ) {
        super(
            store,
            currentLoader,
            compiler,
            parser,
            missingTranslationHandler,
            useDefaultLang,
            useStore,
            useExtend,
            defaultLang
        );
        this.currentLang = this.defaultLang || 'fr'; // Initialize currentLang
    }

    getMultilingualFieldValue(field: any) {
        // any -> Map<string, string>, lang -> 'fr', 'en', 'es', 'de'
        let locale = this.languages.find((l) => l.code === this.currentLang)?.locale;
        if (locale) {
            if (field?.[locale] || field?.[this.localeDefault]) {
                return field[locale] ? field[locale] : field[this.localeDefault];
            } else {
                if (typeof field == 'object') {
                    try {
                        if (field?.locale || field.fr_FR) {
                            return field?.locale ? field.locale : field.fr_FR;
                        } else {
                            return field.get(locale) ? field.get(locale) : null;
                        }
                    } catch (e) {
                        return null;
                    }
                } else {
                    return null;
                }
            }
        } else {
            return null;
        }
    }

    getMultilingualFieldValueWithEnglishDefault(field: any) {
        const langCurrent = localStorage.getItem('lang') ?? 'en';
        let locale = this.languages.find((l) => l.code == langCurrent)?.locale;

        if (locale) {
            if (field?.[locale] || field?.[this.localeEnglish]) {
                if (field[locale]) {
                    return field[locale];
                } else {
                    const fallbackValue = field[this.localeEnglish];
                    const fallbackAsNumber = Number(fallbackValue);
                    if (!isNaN(fallbackAsNumber)) {
                        return 0;
                    } else {
                        return fallbackValue;
                    }
                }
            } else {
                if (typeof field === 'object') {
                    try {
                        if (field?.locale || field.en_GB) {
                            const fallbackValue = field?.locale ?? field.en_GB;
                            const fallbackAsNumber = Number(fallbackValue);
                            if (!isNaN(fallbackAsNumber)) {
                                return 0;
                            } else {
                                return fallbackValue;
                            }
                        } else {
                            const value = field.get?.(locale);
                            const fallbackAsNumber = Number(value);
                            if (!isNaN(fallbackAsNumber)) {
                                return 0;
                            } else {
                                return (
                                    value ?? this.translatePipe.transform('common.NOT_AVAILABLE')
                                );
                            }
                        }
                    } catch (e) {
                        return this.translatePipe.transform('common.NOT_AVAILABLE');
                    }
                } else {
                    return this.translatePipe.transform('common.NOT_AVAILABLE');
                }
            }
        } else {
            return this.translatePipe.transform('common.NOT_AVAILABLE');
        }
    }

    getMultilingualFieldValueWithEnglishDefaultTable(field: any) {
        const langCurrent = localStorage.getItem('lang') ?? 'en';
        let locale = this.languages.find((l) => l.code == langCurrent)?.locale;
        if (locale) {
            if (field?.[locale] || field?.[this.localeEnglish]) {
                return field[locale] ? field[locale] : field[this.localeEnglish];
            } else {
                if (typeof field == 'object') {
                    try {
                        if (field?.locale || field.en_GB) {
                            return field?.locale ? field.locale : field.en_GB;
                        } else {
                            return field.get(locale) ? field.get(locale) : '';
                        }
                    } catch (e) {
                        return '';
                    }
                } else {
                    return '';
                }
            }
        } else {
            return '';
        }
    }
}
